import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// IMPORTANTE: Agregamos Star y CheckCircle2 para el botón de reseñas
import { User, Mail, Phone, Lock, Save, Package, ChevronRight, Calendar, ShieldCheck, Star, CheckCircle2 } from 'lucide-react';
import api from '../../api/axios';
import ReviewModal from './ReviewModal';

const ProfilePage = () => {
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [selectedShipmentId, setSelectedShipmentId] = useState(null);
    const navigate = useNavigate(); 
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', password: '', password_confirmation: ''
    });

    const handleOpenReview = (shipmentId) => {
        setSelectedShipmentId(shipmentId);
        setIsReviewModalOpen(true);
    };

    useEffect(() => { fetchProfile(); }, []);

    const fetchProfile = async () => {
        try {
            const res = await api.get('/user/profile');
            const data = res.data.data || res.data;
            setProfile(data);
            setFormData({
                name: data.user.name,
                email: data.user.email,
                phone: data.user.phone || '',
                password: '',
                password_confirmation: ''
            });
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await api.put('/user/profile', formData);
            setIsEditing(false);
            fetchProfile();
        } catch (err) { console.error("Fallo en la actualización", err); }
    };

    if (loading) return (
        <div className="h-screen flex items-center justify-center font-black text-[#CE010B] animate-pulse uppercase tracking-widest">
            SINCRONIZANDO...
        </div>
    );

    const elementGradient = `
        bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] 
        dark:from-[#780000] dark:to-[#590000]
        border border-white/20 dark:border-white/5
    `;

    return (
        <div className="min-h-screen bg-transparent pt-28 pb-12 px-4 lg:py-12 lg:px-8 relative">
            <div className="w-3/4 mx-auto lg:w-full space-y-10 animate-in fade-in slide-in-from-bottom-6 duration-700">
                
                {/* ENCABEZADO */}
                <header className={`${elementGradient} flex flex-col lg:flex-row justify-between items-center p-6 lg:p-10 rounded-[2.5rem] lg:rounded-[4rem] shadow-2xl transition-all duration-500`}>
                    <div className="flex flex-col lg:flex-row items-center gap-8">
                        <div className="relative">
                            <div className="w-32 h-32 bg-[#CE010B] dark:bg-black/40 rounded-[2.5rem] flex items-center justify-center text-white shadow-2xl">
                                <User size={60} strokeWidth={2.5} />
                            </div>
                            <div className="absolute -bottom-2 -right-2 bg-green-500 p-2 rounded-xl border-4 border-[#F1F1F1] dark:border-[#780000]">
                                <ShieldCheck size={20} className="text-white" />
                            </div>
                        </div>
                        <div className="text-center lg:text-left space-y-2">
                            <h1 className="text-5xl lg:text-5xl font-black uppercase tracking-tighter text-black dark:text-white">
                                {profile?.user?.name}
                            </h1>
                            <p className="text-lg lg:text-[10px] font-black text-black/60 dark:text-white/60 uppercase tracking-[0.1em] lg:tracking-[0.4em] italic flex items-center justify-center lg:justify-start gap-2">
                                <Calendar size={14} /> Socio desde {profile?.user?.joined_at}
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={() => setIsEditing(!isEditing)} 
                        className="mt-6 lg:mt-0 px-6 lg:px-10 py-4 rounded-2xl font-black uppercase text-lg lg:text-xs transition-all duration-300 shadow-xl
                            bg-black text-white hover:scale-105
                            dark:bg-white dark:text-[#590000] dark:hover:bg-gray-200"
                    >
                        {isEditing ? 'Cancelar' : 'Editar Perfil'}
                    </button>
                </header>

                {/* FORMULARIO */}
                <form onSubmit={handleUpdate} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <EditableField icon={<User />} label="Nombre" value={formData.name} isEditing={isEditing} gradientClass={elementGradient}
                        onChange={(e) => setFormData({...formData, name: e.target.value})} />
                    
                    <EditableField icon={<Mail />} label="Correo" type="email" value={formData.email} isEditing={isEditing} gradientClass={elementGradient}
                        onChange={(e) => setFormData({...formData, email: e.target.value})} />

                    <EditableField icon={<Phone />} label="Teléfono" type="tel" value={formData.phone} isEditing={isEditing} gradientClass={elementGradient}
                        onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})} />

                    <div className={`${elementGradient} p-5 lg:p-8 rounded-[2rem] lg:rounded-[3rem] shadow-lg`}>
                        <div className="flex items-center justify-center lg:justify-start gap-4 mb-4">
                            <div className="p-3 bg-black/10 dark:bg-white/10 text-black dark:text-white rounded-2xl"><Lock size={20} /></div>
                            <p className="text-[13px] lg:text-[10px] font-black uppercase text-black/50 dark:text-white/50 tracking-widest">Seguridad</p>
                        </div>
                        {isEditing ? (
                            <div className="space-y-4">
                                <input type="password" placeholder="Nueva contraseña" 
                                    className="w-full bg-white/50 dark:bg-black/20 border-none rounded-2xl p-4 text-2xl lg:text-xl font-bold outline-none focus:ring-2 focus:ring-[#CE010B] dark:text-white text-center lg:text-left" 
                                    onChange={(e) => setFormData({...formData, password: e.target.value})} />
                                <input type="password" placeholder="Confirmar" 
                                    className="w-full bg-white/50 dark:bg-black/20 border-none rounded-2xl p-4 text-2xl lg:text-xl font-bold outline-none focus:ring-2 focus:ring-[#CE010B] dark:text-white text-center lg:text-left" 
                                    onChange={(e) => setFormData({...formData, password_confirmation: e.target.value})} />
                            </div>
                        ) : <p className="text-2xl lg:text-xl font-black tracking-[0.5em] text-black/30 dark:text-white/30 pt-2 text-center lg:text-left">••••••••</p>}
                    </div>

                    {isEditing && (
                        <div className="lg:col-span-2 flex justify-center pt-6">
                            <button type="submit" className="flex items-center gap-4 bg-[#CE010B] text-white px-20 py-6 rounded-[3rem] font-black uppercase tracking-[0.2em] hover:bg-black hover:scale-105 transition-all shadow-2xl shadow-black/20">
                                <Save size={24} /> Guardar Cambios
                            </button>
                        </div>
                    )}
                </form>
                {/* ENVÍOS */}
                <section className="space-y-8 pt-10 text-black dark:text-white">
                    <h3 className="text-4xl lg:text-3xl font-black uppercase tracking-tighter text-white">Mis Envíos</h3>
                    <div className="grid gap-5">
                        {profile?.shipments?.length > 0 ? profile.shipments.map((ship) => (
                            <div 
                                key={ship.id} 
                                onClick={() => navigate(`/rastreo?guia=${ship.tracking_number}`)}
                                className={`${elementGradient} p-5 lg:p-8 rounded-[2rem] lg:rounded-[2.5rem] flex flex-col lg:flex-row lg:items-center justify-between gap-6 group hover:scale-[1.02] active:scale-95 cursor-pointer transition-all duration-500 shadow-sm`}
                            >
                                <div className="flex items-center gap-8">
                                    <div className="p-5 bg-black/5 dark:bg-white/10 rounded-[2rem] text-black dark:text-white">
                                        <Package size={30} />
                                    </div>
                                    <div>
                                        <p className="font-black text-[#CE010B] dark:text-white/60 text-lg lg:text-xs tracking-widest">#{ship.tracking_number}</p>
                                        <p className="font-black text-3xl lg:text-lg lg:font-bold uppercase">{ship.recipient_name}</p>
                                    </div>
                                </div>
                                
                                {/* CONTENEDOR DE ESTATUS Y RESEÑA */}
                                <div className="flex flex-row lg:flex-col items-center lg:items-end justify-between gap-3">
                                    <div className="flex items-center gap-4">
                                        <span className="px-5 py-1.5 rounded-xl text-base lg:text-[11px] font-black uppercase bg-black/10 dark:bg-white/10">
                                            {ship.current_status}
                                        </span>
                                        <ChevronRight className="hidden lg:block text-black/30 dark:text-white/30 group-hover:text-[#CE010B] transition-colors" size={28} />
                                    </div>

                                    {/* LÓGICA DEL BOTÓN DE RESEÑA */}
                                    {ship.current_status?.toLowerCase() === 'entregado' && (
                                        !ship.review ? (
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Evita que se abra la página de rastreo
                                                    handleOpenReview(ship.id);
                                                }}
                                                className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black text-base lg:text-[10px] lg:px-4 lg:py-2 font-black uppercase tracking-widest rounded-xl lg:rounded-lg shadow-md hover:scale-105 active:scale-95 transition-all flex items-center gap-2 z-10"
                                            >
                                                <Star className="w-5 h-5 lg:w-3 lg:h-3 fill-black" /> Calificar Envío
                                            </button>
                                        ) : (
                                            <span className="text-base lg:text-[10px] font-black text-green-600 dark:text-green-400 flex items-center gap-1.5 uppercase tracking-widest">
                                                <CheckCircle2 className="w-5 h-5 lg:w-3 lg:h-3" /> Reseña Publicada
                                            </span>
                                        )
                                    )}
                                </div>
                            </div>
                        )) : <p className="py-20 text-center font-bold opacity-30 italic uppercase">Sin envíos registrados</p>}
                    </div>
                </section>
            </div>

            {/* MODAL DE RESEÑAS */}
            <ReviewModal 
                isOpen={isReviewModalOpen} 
                onClose={() => setIsReviewModalOpen(false)} 
                shipmentId={selectedShipmentId}
                onReviewSuccess={fetchProfile} 
            />
        </div>
    );
};

const EditableField = ({ icon, label, value, isEditing, onChange, gradientClass, type = "text" }) => (
    <div className={`${gradientClass} p-5 lg:p-8 rounded-[2rem] lg:rounded-[3rem] shadow-lg`}>
        <div className="flex items-center justify-center lg:justify-start gap-4 mb-3">
            <div className="p-3 bg-black/10 dark:bg-white/10 text-black dark:text-white rounded-2xl">{icon}</div>
            <p className="text-[13px] lg:text-[10px] font-black uppercase text-black/50 dark:text-white/50 tracking-widest">{label}</p>
        </div>
        {isEditing ? <input type={type} value={value} onChange={onChange} className="w-full bg-white/50 dark:bg-black/20 border-none rounded-2xl p-4 text-2xl lg:text-xl font-bold focus:ring-2 focus:ring-[#CE010B] outline-none text-black dark:text-white text-center lg:text-left" required /> 
                    : <p className="text-2xl lg:text-xl font-black truncate text-center lg:text-left">{value || '---'}</p>}
    </div>
);

export default ProfilePage;